import { action, computed, makeObservable, observable } from 'mobx'

import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import { INameValuePair } from '~/client/src/shared/interfaces/INameValuePair'
import KnownTranslatorKeys from '~/client/src/shared/localization/knownTranslatorKeys'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'
import UserProject from '~/client/src/shared/models/UserProject'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import { IServiceUserDto } from '~/client/src/shared/types/UserDto'
import { formatBulkSaveError } from '~/client/src/shared/utils/bulkSaveErrorFormatter'
import Guard from '~/client/src/shared/utils/Guard'
import isWeakPassword from '~/client/src/shared/utils/isWeakPassword'
import {
  formatPhoneNumber,
  getFormattedPhoneNumberForSubmission,
  wasPhoneCleared,
} from '~/client/src/shared/utils/phoneNumberHelpers'
import { isPhoneNumberValidForSubmission } from '~/client/src/shared/utils/phoneNumberValidation'

export enum UserDetailsSections {
  PROFILE = 'PROFILE',
  PASSWORD_CHANGER = 'PASSWORD_CHANGER',
}

export interface IUserDetailsField {
  id: FieldIds
  value?: string | string[]
  defaultValue?: string
  type: FieldTypes
  section: UserDetailsSections
  getOptions?: () => INameValuePair[]
  onChange?: (event: any) => void
  onSelectChange?: (event: any) => void
  onButtonClick?: (event: any) => void
  additionalTextTranslatorKey?: KnownTranslatorKeys
  nameTranslatorKey?: KnownTranslatorKeys
  isValid?: boolean
  isRequired?: boolean
  hideButton?: boolean
}

export enum FieldIds {
  FIRST_NAME = 'first-name',
  AVATAR = 'avatar',
  LAST_NAME = 'last-name',
  JOB_TITLE = 'job-title',
  PHONE_NUMBER = 'phone-number',
  COMPANY = 'company',
  PASSWORD = 'password',
  NEW_PASSWORD = 'new-password',
  CONFIRM_NEW_PASSWORD = 'confirm-new-password',
  EMAIL = 'email',
}
export enum FieldTypes {
  INPUT = 'input',
  AVATAR = 'avatar',
  PASSWORD = 'password',
  SELECT = 'select',
  EMAIL = 'email',
  PHONE = 'phone',
  TAGS = 'tags',
}

// /set-password codes that mean the current password was the problem.
const CURRENT_PASSWORD_ERROR_CODES = new Set([
  'invalid_current_password',
  'too_many_attempts',
])

export default class ProfileStore {
  @observable public fields: IUserDetailsField[] = []
  @observable public shouldDisableSubmitButton: boolean = true
  @observable public shouldShowModal: boolean = false
  @observable public warningMessage: string = null
  // STX1-637: strand-confirmation message when clearing the phone would cost
  // the user the ability to sign in; null when no prompt is pending.
  @observable public phoneStrandPrompt: string = null

  public constructor(
    private readonly eventsStore: DesktopEventStore,
    private readonly projectMembersStore: ProjectMembersStore,
    companiesStore: CompaniesStore,
  ) {
    Guard.requireAll({
      eventsStore,
      companiesStore,
      projectMembersStore,
    })

    const firstNameField: IUserDetailsField = {
      id: FieldIds.FIRST_NAME,
      value: '',
      type: FieldTypes.INPUT,
      nameTranslatorKey: KnownTranslatorKeys.name,
      section: UserDetailsSections.PROFILE,
    }

    const avatarField: IUserDetailsField = {
      id: FieldIds.AVATAR,
      value: '',
      type: FieldTypes.AVATAR,
      nameTranslatorKey: KnownTranslatorKeys.name,
      section: UserDetailsSections.PROFILE,
    }

    const lastNameField: IUserDetailsField = {
      id: FieldIds.LAST_NAME,
      value: '',
      type: FieldTypes.INPUT,
      additionalTextTranslatorKey:
        KnownTranslatorKeys.userProfileDescriptions_name,
      section: UserDetailsSections.PROFILE,
    }
    const jobTitleField: IUserDetailsField = {
      id: FieldIds.JOB_TITLE,
      value: '',
      type: FieldTypes.INPUT,
      nameTranslatorKey: KnownTranslatorKeys.globalRole,
      section: UserDetailsSections.PROFILE,
    }
    const companyField: IUserDetailsField = {
      id: FieldIds.COMPANY,
      value: '',
      type: FieldTypes.SELECT,
      isRequired: true,
      nameTranslatorKey: KnownTranslatorKeys.company,
      section: UserDetailsSections.PROFILE,
      getOptions: () =>
        companiesStore.getCompaniesAsOptions(
          this.userActiveProjectSettings.companyId,
        ),
    }
    const emailField: IUserDetailsField = {
      id: FieldIds.EMAIL,
      value: '',
      type: FieldTypes.EMAIL,
      nameTranslatorKey: KnownTranslatorKeys.email_noun,
      section: UserDetailsSections.PROFILE,
    }
    const phoneNumberField: IUserDetailsField = {
      id: FieldIds.PHONE_NUMBER,
      value: '',
      type: FieldTypes.PHONE,
      nameTranslatorKey: KnownTranslatorKeys.phone,
      isValid: true,
      section: UserDetailsSections.PROFILE,
      defaultValue: this.appState.countryCode,
    }
    const passwordField: IUserDetailsField = {
      id: FieldIds.PASSWORD,
      value: '',
      type: FieldTypes.PASSWORD,
      nameTranslatorKey: KnownTranslatorKeys.currentPassword,
      isValid: true,
      section: UserDetailsSections.PASSWORD_CHANGER,
    }
    const newPasswordField: IUserDetailsField = {
      id: FieldIds.NEW_PASSWORD,
      value: '',
      type: FieldTypes.PASSWORD,
      nameTranslatorKey: KnownTranslatorKeys.newPassword,
      isValid: true,
      section: UserDetailsSections.PASSWORD_CHANGER,
    }
    const confirmNewPasswordField: IUserDetailsField = {
      id: FieldIds.CONFIRM_NEW_PASSWORD,
      value: '',
      type: FieldTypes.PASSWORD,
      nameTranslatorKey: KnownTranslatorKeys.confirmNewPassword,
      isValid: true,
      section: UserDetailsSections.PASSWORD_CHANGER,
    }
    makeObservable(this)

    this.fields = [
      firstNameField,
      lastNameField,
      jobTitleField,
      companyField,
      emailField,
      phoneNumberField,
      passwordField,
      newPasswordField,
      confirmNewPasswordField,
      avatarField,
    ]

    this.fields.forEach(field => {
      switch (field.id) {
        case FieldIds.PHONE_NUMBER:
          field.onChange = this.onPhoneNumberChange.bind(this, field)
          break
        case FieldIds.EMAIL:
          break
        default:
          field.onChange = this.onChange.bind(this, field)
          break
      }
    })
  }

  public updateAvatarField = (url: string) => {
    if (this.avatarField.value !== url) {
      this.shouldDisableSubmitButton = false
    }
    this.avatarField.value = url
  }

  public get avatarField(): IUserDetailsField {
    return this.fields.find(field => field.id === FieldIds.AVATAR)
  }

  private get appState(): DesktopInitialState {
    return this.eventsStore.appState
  }

  public get isUpdating(): boolean {
    return this.appState.loading.get(e.SAVE_MEMBERS)
  }

  public get isPasswordChanging() {
    return this.appState.loading.get(e.CHANGE_PASSWORD)
  }

  public get isPasswordSaving() {
    return this.appState.loading.get(e.SET_PASSWORD)
  }

  public get currentUser(): User {
    return this.appState.user
  }

  public get userActiveProjectSettings(): UserProject {
    return this.appState.userActiveProjectSettings
  }

  public get passwordChangeError(): string {
    const { PASSWORD } = FieldIds
    const oldPassword = this.getFieldValue(PASSWORD)

    if (!oldPassword) {
      return ' '
    }

    return this.validateNewPasswordFields()
  }

  // STX1-366: set-password mode skips the "old password" field, so its
  // validation only covers the new-password pair.
  public get passwordSetError(): string {
    return this.validateNewPasswordFields()
  }

  private validateNewPasswordFields(): string {
    const { NEW_PASSWORD, CONFIRM_NEW_PASSWORD } = FieldIds
    const newPassword = this.getFieldValue(NEW_PASSWORD) as string
    const confirmedPassword = this.getFieldValue(CONFIRM_NEW_PASSWORD)

    if (!newPassword) {
      return ' '
    }

    if (isWeakPassword(newPassword)) {
      return Localization.translator.passwordErrors.weakPassword
    }

    if (newPassword !== confirmedPassword) {
      return Localization.translator.passwordErrors.doNotMatch
    }

    return null
  }

  public getFieldById(id: FieldIds): IUserDetailsField {
    return this.fields.find(field => field.id === id)
  }

  @action.bound
  public assignDefaultValuesToFields() {
    const { phoneNumber, firstName, lastName, globalRole, email, avatarUrl } =
      this.currentUser

    this.getFieldById(FieldIds.PHONE_NUMBER).value = phoneNumber || ''
    this.avatarField.value = avatarUrl

    this.getFieldById(FieldIds.FIRST_NAME).value = firstName
    this.getFieldById(FieldIds.LAST_NAME).value = lastName
    this.getFieldById(FieldIds.JOB_TITLE).value = globalRole || ''
    this.getFieldById(FieldIds.EMAIL).value = email

    this.getFieldById(FieldIds.COMPANY).value =
      this.userActiveProjectSettings.companyId

    this.shouldDisableSubmitButton = true
    this.setAllPasswordFieldsValid()
  }

  @action.bound
  public async updateProfile() {
    this.resetWarningMessage()

    this.shouldDisableSubmitButton = true

    // Pristine loaded value — save mutates a getCopy(), not currentUser itself.
    const previousPhone = this.currentUser.phoneNumber

    const editingUserJson = this.currentUser.getCopy() as IServiceUserDto

    editingUserJson.avatarUrl = this.avatarField.value as string
    editingUserJson.phoneNumber = getFormattedPhoneNumberForSubmission(
      this.getFieldValue(FieldIds.PHONE_NUMBER) as string,
    )
    editingUserJson.firstName = this.getFieldValue(
      FieldIds.FIRST_NAME,
    ) as string
    editingUserJson.lastName = this.getFieldValue(FieldIds.LAST_NAME) as string
    editingUserJson.globalRole = (
      (this.getFieldValue(FieldIds.JOB_TITLE) as string) || ''
    ).trim()

    // update userProject section; project roles are admin-managed and kept as is
    const userProjectSettings = this.userActiveProjectSettings.toDto()

    userProjectSettings.companyId = this.getFieldValue(FieldIds.COMPANY) || null
    editingUserJson.userProjectSettings = userProjectSettings

    try {
      await this.projectMembersStore.saveMembers([editingUserJson])
    } catch (e) {
      console.warn(e)
      // Warning banner is single-line — join row details inline
      this.setWarningMessage(formatBulkSaveError(e, '; '))
      // Save failed → skip the phone removal.
      this.openAlertModal()
      return
    }

    // STX1-637: emptying the phone in the profile form removes it everywhere
    // (central + Firebase + tenants). saveMembers only preserves a blank, so
    // dispatch the removal explicitly after the save succeeds.
    if (wasPhoneCleared(previousPhone, editingUserJson.phoneNumber)) {
      const result = await this.projectMembersStore.removeMemberPhone(
        this.currentUser.id,
      )
      if (result.outcome === 'confirm-strand') {
        // Defer the success alert; the strand ConfirmDialog drives the choice.
        this.phoneStrandPrompt = result.message
        return
      }
      if (result.outcome === 'error') {
        this.setWarningMessage(result.message)
      } else {
        this.currentUser.phoneNumber = ''
      }
    }

    this.openAlertModal()
  }

  @action.bound
  public async confirmPhoneClear() {
    const message = this.phoneStrandPrompt
    this.phoneStrandPrompt = null
    if (message === null) {
      return
    }

    const result = await this.projectMembersStore.removeMemberPhone(
      this.currentUser.id,
      true,
    )
    if (result.outcome === 'error') {
      this.setWarningMessage(result.message)
    } else {
      this.currentUser.phoneNumber = ''
    }
    this.openAlertModal()
  }

  @action.bound
  public cancelPhoneClear() {
    this.phoneStrandPrompt = null
    // Keep the number: restore the field to the loaded value.
    this.getFieldById(FieldIds.PHONE_NUMBER).value =
      this.currentUser.phoneNumber || ''
  }

  @action.bound
  public changePassword() {
    this.setAllPasswordFieldsValid()
    this.resetWarningMessage()

    this.eventsStore.dispatch(
      e.CHANGE_PASSWORD,
      this.getFieldValue(FieldIds.NEW_PASSWORD),
      // The auth service re-verifies the current password before changing it.
      this.getFieldValue(FieldIds.PASSWORD),
      this.handlePasswordChangeSuccess,
      this.handlePasswordChangeError,
    )
  }

  // STX1-366: first-time set from profile (user skipped the invite-link
  // prompt or signed in with phone OTP). Dispatches through the backend so
  // /set-password runs and syncs across STX + tenant Firebase.
  @action.bound
  public setPassword() {
    this.setAllPasswordFieldsValid()
    this.resetWarningMessage()

    this.eventsStore.dispatch(
      e.SET_PASSWORD,
      this.getFieldValue(FieldIds.NEW_PASSWORD),
      this.handlePasswordChangeSuccess,
      this.handlePasswordChangeError,
    )
  }

  @action.bound
  public closeAlertModal() {
    this.shouldShowModal = false
  }

  @action.bound
  public openAlertModal() {
    this.shouldShowModal = true
  }

  @computed
  public get areAllRequiredFieldsFilled(): boolean {
    return this.fields.filter(f => f.isRequired).every(field => !!field.value)
  }

  public get isSubmitBtnDisabled(): boolean {
    return this.shouldDisableSubmitButton || !this.areAllRequiredFieldsFilled
  }

  @action.bound
  private handlePasswordChangeError({
    message,
    code,
  }: Error & { code?: string }) {
    this.setWarningMessage(message)

    if (CURRENT_PASSWORD_ERROR_CODES.has(code)) {
      // The current password was rejected: flag and clear that field, keep the
      // new password the user already typed twice.
      this.getFieldById(FieldIds.PASSWORD).isValid = false
      this.getFieldById(FieldIds.PASSWORD).value = ''
    } else {
      this.setNewPasswordFieldsInvalid()
      this.resetPasswordFields(false)
    }

    this.openAlertModal()
  }

  @action.bound
  private handlePasswordChangeSuccess() {
    this.resetPasswordFields()
    this.openAlertModal()
  }

  private onPhoneNumberChange(field: IUserDetailsField, phoneNumber: string) {
    field.value = formatPhoneNumber(phoneNumber)

    field.isValid = isPhoneNumberValidForSubmission(phoneNumber)
    this.shouldDisableSubmitButton = !field.isValid
  }

  @action.bound
  private onChange(field: IUserDetailsField, newValue: string) {
    if (
      field.value !== newValue &&
      field.section === UserDetailsSections.PROFILE
    ) {
      this.shouldDisableSubmitButton = false
    }
    field.value = newValue
  }

  private resetPasswordFields(shouldResetOldPwd: boolean = true) {
    const { PASSWORD, NEW_PASSWORD, CONFIRM_NEW_PASSWORD } = FieldIds

    if (shouldResetOldPwd) {
      this.getFieldById(PASSWORD).value = ''
    }

    this.getFieldById(NEW_PASSWORD).value = ''
    this.getFieldById(CONFIRM_NEW_PASSWORD).value = ''
  }

  private setWarningMessage(message: string) {
    this.warningMessage = message
  }

  private resetWarningMessage() {
    this.warningMessage = null
  }

  private setOldPasswordFieldValid() {
    this.getFieldById(FieldIds.PASSWORD).isValid = true
  }

  private setNewPasswordFieldsValid() {
    this.getFieldById(FieldIds.NEW_PASSWORD).isValid = true
    this.getFieldById(FieldIds.CONFIRM_NEW_PASSWORD).isValid = true
  }

  private setNewPasswordFieldsInvalid() {
    this.getFieldById(FieldIds.NEW_PASSWORD).isValid = false
    this.getFieldById(FieldIds.CONFIRM_NEW_PASSWORD).isValid = false
  }

  private setAllPasswordFieldsValid() {
    this.setOldPasswordFieldValid()
    this.setNewPasswordFieldsValid()
  }

  private getFieldValue(id: FieldIds) {
    return this.getFieldById(id).value
  }
}
